import React, { useState, useEffect, useCallback, useRef } from 'react';
import Header from './components/Header';
import Hero from './components/Hero';
import PortfolioGrid from './components/PortfolioGrid';
import Pillars from './components/Pillars';
import Process from './components/Process';
import Footer from './components/Footer';
import SectionWrapper from './components/SectionWrapper';
import ProjectDetailOverlay from './components/ProjectDetailOverlay';
import { CASE_STUDIES } from './constants';
import { CaseStudy } from './types';

const App: React.FC = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [selectedStudy, setSelectedStudy] = useState<CaseStudy | null>(null);
  const isAnimating = useRef(false);
  const scrollAccumulator = useRef(0);
  const lastScrollTime = useRef(0);

  const goToSection = (index: number) => {
    if (isAnimating.current || index < 0 || index >= sections.length) return;
    isAnimating.current = true;
    setActiveIndex(index);
    // Section transition duration is 1s in CSS, matching here
    setTimeout(() => { 
      isAnimating.current = false; 
      scrollAccumulator.current = 0;
    }, 1000);
  };
  
  const sections = [
    { id: 'hero', component: <Hero onLaunchPortfolio={() => goToSection(1)} onMeetStudio={() => goToSection(2)} /> },
    { id: 'work', component: <PortfolioGrid onOpenDetail={(s) => setSelectedStudy(s)} /> },
    { id: 'pillars', component: <Pillars /> },
    { id: 'process', component: <Process /> },
    { id: 'cta', component: (
      <section className="h-full w-full flex items-center justify-center bg-primary text-white text-center px-6">
        <div className="max-w-5xl mx-auto flex flex-col gap-8 md:gap-12">
          <h2 className="text-6xl lg:text-[9rem] font-black uppercase leading-[0.85] tracking-tighter italic">
            Have an idea worth building?
          </h2>
          <p className="text-2xl lg:text-3xl font-medium max-w-2xl mx-auto text-white/90">
            We're currently accepting new projects. Let's make something legendary together.
          </p>
          <div className="flex flex-col items-center gap-8 mt-4">
            <a 
              href="mailto:rob@theapps.io"
              className="bg-black text-white px-16 py-8 font-black text-2xl tracking-[0.2em] uppercase hover:bg-white hover:text-black transition-all shadow-2xl"
            >
              Start a Conversation
            </a>
            <div className="flex flex-col items-center">
              <span className="text-black/40 font-black uppercase tracking-[0.4em] text-[10px] mb-2">Direct Studio Support</span>
              <a href="mailto:rob@theapps.io" className="text-black font-black uppercase tracking-[0.3em] text-xl border-b-2 border-black/10 hover:border-black transition-all">
                rob@theapps.io
              </a>
            </div>
          </div>
        </div>
      </section>
    )},
    { id: 'footer', component: (
      <div className="h-full flex flex-col justify-end bg-black">
        <div className="flex-1 flex items-center justify-center px-6">
           <h2 className="text-[12vw] font-black text-white/5 uppercase tracking-tighter leading-none select-none">T.H.E STUDIO</h2>
        </div>
        <Footer />
      </div>
    )}
  ];

  const handleScrollAction = useCallback((delta: number) => {
    if (isAnimating.current || selectedStudy) return;
    
    // threshold: total distance required to trigger a page jump
    const threshold = 60; 
    
    // Accumulate delta to prevent hair-trigger jumps
    scrollAccumulator.current += delta;

    if (scrollAccumulator.current > threshold && activeIndex < sections.length - 1) {
      goToSection(activeIndex + 1);
    } else if (scrollAccumulator.current < -threshold && activeIndex > 0) {
      goToSection(activeIndex - 1);
    }
    
    // Clear accumulator if no activity for a while
    const now = Date.now();
    if (now - lastScrollTime.current > 500) {
      scrollAccumulator.current = delta;
    }
    lastScrollTime.current = now;

  }, [activeIndex, sections.length, selectedStudy]);

  useEffect(() => {
    const onWheel = (e: WheelEvent) => {
      if (selectedStudy || isAnimating.current) return;

      let current: HTMLElement | null = e.target as HTMLElement;
      let scrollableContainer: HTMLElement | null = null;
      
      // Find the nearest scrollable parent
      while (current && current !== document.body) {
        const style = window.getComputedStyle(current);
        const isScrollable = (style.overflowY === 'auto' || style.overflowY === 'scroll');
        const hasContentToScroll = current.scrollHeight > current.clientHeight;
        
        if (isScrollable && hasContentToScroll) {
          scrollableContainer = current;
          break;
        }
        current = current.parentElement;
      }

      if (scrollableContainer) {
        const { scrollTop, scrollHeight, clientHeight } = scrollableContainer;
        // Robust tolerance for desktop browsers
        const atTop = scrollTop <= 5;
        const atBottom = Math.ceil(scrollTop + clientHeight) >= scrollHeight - 5;

        if ((atTop && e.deltaY < 0) || (atBottom && e.deltaY > 0)) {
          // If at boundary and scrolling "out", we capture the event for section transition
          e.preventDefault();
          handleScrollAction(e.deltaY);
        } else {
          // Allow normal scroll within the container
          // We reset the accumulator to prevent a jump immediately after finishing internal scroll
          scrollAccumulator.current = 0;
          return;
        }
      } else {
        // No scrollable container, handle page transition directly
        e.preventDefault();
        handleScrollAction(e.deltaY);
      }
    };

    let touchStart = 0;
    const onTouchStart = (e: TouchEvent) => {
      touchStart = e.touches[0].clientY;
    };
    
    const onTouchEnd = (e: TouchEvent) => {
      if (selectedStudy || isAnimating.current) return;
      
      const touchEnd = e.changedTouches[0].clientY;
      const delta = touchStart - touchEnd;

      // Swipe must be significant
      if (Math.abs(delta) < 40) return;

      let current: HTMLElement | null = e.target as HTMLElement;
      let isInsideScrollable = false;
      
      while (current && current !== document.body) {
        const style = window.getComputedStyle(current);
        const isScrollable = (style.overflowY === 'auto' || style.overflowY === 'scroll');
        const hasContentToScroll = current.scrollHeight > (current.clientHeight + 10); // 10px buffer for mobile chrome bars

        if (isScrollable && hasContentToScroll) {
          const atTop = current.scrollTop <= 15;
          const atBottom = Math.ceil(current.scrollTop + current.clientHeight) >= current.scrollHeight - 15;
          
          // Only mark as "inside scrollable" if we aren't at the very edge swiping outwards
          // delta > 0 means swiping UP (scrolling DOWN)
          // delta < 0 means swiping DOWN (scrolling UP)
          const swipingOutwardsAtBottom = atBottom && delta > 0;
          const swipingOutwardsAtTop = atTop && delta < 0;

          if (!(swipingOutwardsAtTop || swipingOutwardsAtBottom)) {
            isInsideScrollable = true;
          }
          break;
        }
        current = current.parentElement;
      }

      if (!isInsideScrollable) {
        // Force transition by providing a sufficient normalized delta
        handleScrollAction(delta > 0 ? 100 : -100);
      }
    };

    window.addEventListener('wheel', onWheel, { passive: false });
    window.addEventListener('touchstart', onTouchStart, { passive: true });
    window.addEventListener('touchend', onTouchEnd, { passive: true });

    return () => {
      window.removeEventListener('wheel', onWheel);
      window.removeEventListener('touchstart', onTouchStart);
      window.removeEventListener('touchend', onTouchEnd);
    };
  }, [handleScrollAction, selectedStudy, activeIndex]);

  return (
    <div className={`relative h-screen w-full bg-background-dark perspective-container select-none transition-transform duration-700 ${selectedStudy ? 'scale-95 opacity-50' : 'scale-100 opacity-100'}`}>
      <div className="fixed inset-0 grain-overlay z-[100] pointer-events-none"></div>
      
      <Header onNavigate={goToSection} activeIndex={activeIndex} />
      
      <main className="relative h-full w-full">
        {sections.map((section, idx) => (
          <SectionWrapper 
            key={section.id} 
            id={section.id} 
            index={idx} 
            activeIndex={activeIndex}
          >
            {section.component}
          </SectionWrapper>
        ))}
      </main>

      <div className={`fixed right-8 top-1/2 -translate-y-1/2 z-[100] flex flex-col gap-4 transition-opacity ${selectedStudy ? 'opacity-0' : 'opacity-100'}`}>
        {sections.map((_, idx) => (
          <div 
            key={idx}
            className={`w-1 h-8 transition-all duration-500 ${idx === activeIndex ? 'bg-primary h-12' : 'bg-white/20'}`}
          />
        ))}
      </div>

      <ProjectDetailOverlay 
        study={selectedStudy} 
        onClose={() => setSelectedStudy(null)} 
      />
    </div>
  );
};

export default App;